import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Volume2, VolumeX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import { setAccountSelectedMenu } from "store/commonSlice";
import { setActiveTourItem } from "store/whatsNewSlice";
import { AccountSelectMenu } from "pages/Account/Account";
import { PATHS } from "router/config/paths";
import { iconFromName } from "components/WhatsNewMenu/whatsNew.data";
import { cn } from "utils/cn";
import { AnalyticsEvent } from "utils";

// Global feature modal. Driven by state.whatsNew.activeTourItem (set when a What's New card is
// clicked, or when a feature page's hero tour opens); after navigation lands on the feature's page
// it renders over it in one of two shapes:
//   • "how_it_works" — a step list on the left drives one big video on the right; only the active
//     ("playing") step's timeline bar fills as its clip plays, and playback auto-advances on end.
//   • "whats_new" — a single looping video on top + a short highlight list + a "Get started" CTA.
const FeatureTourModal = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const feature = useAppSelector((s) => s.whatsNew.activeTourItem);

  const steps = feature?.how_it_works?.steps ?? [];
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0); // 0..1 within the ACTIVE step's clip
  const [muted, setMuted] = useState(false); // tour audio defaults ON; toggle in the video's top-right
  // One <video> per step, all mounted & preloaded and stacked; we only fade/play the active one.
  // Each step has its own clip in production, so switching src on a single element would flash a
  // black frame while the next clip loads — preloaded siblings crossfade instead.
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  const close = () => dispatch(setActiveTourItem(null));

  // On step change: restart+play the active clip, pause the rest. Audio defaults on; if the browser
  // blocks sound-autoplay (e.g. an auto-opened tour with no user gesture), fall back to muted so the
  // clip still plays and reflect that in the toggle.
  useEffect(() => {
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === activeIndex) {
        v.currentTime = 0;
        v.play().catch(() => {
          if (!v.muted) {
            v.muted = true;
            setMuted(true);
            v.play().catch(() => undefined);
          }
        });
      } else {
        v.pause();
      }
    });
  }, [activeIndex]);

  // "Want to talk with an expert?" → Account → Support with the Submit-a-Case form already open.
  const goGetInTouch = () => {
    close();
    dispatch(setAccountSelectedMenu(AccountSelectMenu.Support));
    navigate(PATHS.ACCOUNT, { state: { openSupportCase: true } });
  };

  useEffect(() => {
    if (feature) AnalyticsEvent("feature_tour_open", "", "click", { feature: feature.id });
    setActiveIndex(0);
    setProgress(0);
    setMuted(false); // each tour opens with sound on
  }, [feature]);

  useEffect(() => {
    if (!feature) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dispatch(setActiveTourItem(null));
    };

    document.addEventListener("keydown", onKey);

    return () => document.removeEventListener("keydown", onKey);
  }, [feature, dispatch]);

  // Lock scrolling behind the modal. The app scrolls in various containers per page (not always the
  // body), so block wheel/touch globally and only allow it inside the modal (marked data-feature-modal).
  useEffect(() => {
    if (!feature) return;

    const preventScroll = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest?.("[data-feature-modal]")) return;
      e.preventDefault();
    };

    document.addEventListener("wheel", preventScroll, { passive: false });
    document.addEventListener("touchmove", preventScroll, { passive: false });

    return () => {
      document.removeEventListener("wheel", preventScroll);
      document.removeEventListener("touchmove", preventScroll);
    };
  }, [feature]);

  if (!feature) return null;

  const mediaUrl = feature.media?.url ?? "";

  // "Want to talk with an expert? Get in touch." — shared under both modals' CTAs.
  const expertPrompt = (
    <p className="text-center text-[14px] text-grey">
      {t("common:whats_new.tour.expert_prompt")}{" "}
      <button onClick={goGetInTouch} className="font-medium text-purpleGreen underline underline-offset-2 hover:opacity-80">
        {t("common:whats_new.tour.get_in_touch")}
      </button>
    </p>
  );

  // "What's New" shape: single looping video on top, highlight list + CTA below. Closes via the CTA,
  // the backdrop, or Escape (no explicit X).
  if (feature.modal_type === "whats_new") {
    const highlights = feature.whats_new?.highlights ?? [];
    const ctaLabel = feature.whats_new?.cta_label || t("common:whats_new.tour.get_started");

    return createPortal(
      <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onMouseDown={close}>
        <div
          data-feature-modal
          className="relative flex w-[92vw] max-w-[440px] max-h-[88vh] flex-col overflow-hidden rounded-2xl border border-black-300 bg-trueWhite dark:bg-black-100 shadow-2xl"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {feature.media?.url && (
            <div className="bg-black">
              {feature.media.type === "image" ? (
                <img src={feature.media.url} alt="" className="block h-auto w-full" />
              ) : (
                <video src={feature.media.url} className="block h-auto w-full" autoPlay muted loop playsInline />
              )}
            </div>
          )}

          <div className="flex flex-col gap-3 overflow-y-auto p-5">
            <div className="flex flex-col gap-1.5">
              <h2 className="text-h6 text-trueBlack dark:text-white">{feature.title}</h2>
              <p className="text-[13px] leading-relaxed text-grey">{feature.description}</p>
            </div>

            <ul className="flex flex-col gap-2.5">
              {highlights.map((h, idx) => {
                const Icon = iconFromName(h.icon);

                return (
                  <li key={idx} className="flex items-center gap-2.5">
                    <Icon className="h-[18px] w-[18px] flex-shrink-0 text-trueBlack dark:text-white" strokeWidth={1.75} />
                    <span className="text-[13px] text-trueBlack dark:text-grey">{h.text}</span>
                  </li>
                );
              })}
            </ul>

            <button onClick={close} className="btn btn-primary-small mt-1 w-full">
              {ctaLabel}
            </button>
            {expertPrompt}
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // "How it works" shape: steps on the left drive one big video on the right. Closes via "Got it",
  // the backdrop, or Escape (no explicit X).
  const howItWorks = feature.how_it_works;
  const tourTitle = howItWorks?.header || feature.title;
  const tourSubtitle = howItWorks?.subtitle || feature.description;

  return createPortal(
    <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onMouseDown={close}>
      <div
        data-feature-modal
        className="relative flex w-[92vw] max-w-[1000px] max-h-[88vh] flex-col overflow-hidden rounded-2xl border border-black-300 bg-trueWhite dark:bg-black-100 shadow-2xl md:block clg:block"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Video — the height driver: the active clip renders at its natural size and the modal hugs it.
            Every step's clip is mounted and preloaded, stacked; the active one is in-flow + visible, the
            rest are absolute + faded out. On desktop it sits in the right 54%; on mobile it stacks on top. */}
        <div className="relative w-full bg-black md:ml-auto md:w-[54%] clg:ml-auto clg:w-[54%]">
          {steps.map((step, i) => {
            const isActive = i === activeIndex;

            return (
              <video
                key={i}
                ref={(el) => {
                  videoRefs.current[i] = el;
                }}
                src={step.video?.url ?? mediaUrl}
                className={cn(
                  "block max-h-[88vh] w-full object-contain transition-opacity duration-200",
                  isActive ? "relative opacity-100" : "pointer-events-none absolute inset-0 h-full w-full opacity-0"
                )}
                autoPlay={isActive}
                muted={isActive ? muted : true}
                playsInline
                preload="auto"
                onTimeUpdate={isActive ? (e) => setProgress(e.currentTarget.duration ? e.currentTarget.currentTime / e.currentTarget.duration : 0) : undefined}
                onEnded={
                  isActive
                    ? () => {
                        setActiveIndex((prev) => (prev + 1) % steps.length);
                        setProgress(0);
                      }
                    : undefined
                }
              />
            );
          })}
          {/* Sound toggle — top-right of the video, same round style as the tooltip expand button. Default on. */}
          <button
            type="button"
            onClick={() => setMuted((m) => !m)}
            className="absolute top-2.5 right-2.5 z-10 flex items-center justify-center h-8 w-8 rounded-full bg-trueBlack/60 text-trueWhite hover:bg-trueBlack/80 transition-colors"
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        </div>

        {/* Steps (left) — overlays the left 46% on desktop at the video's height and scrolls; stacks
            below the video on mobile. Two states only: playing (active) and not selected. */}
        <div className="flex w-full flex-col bg-trueWhite dark:bg-black-100 md:absolute md:inset-y-0 md:left-0 md:w-[46%] clg:absolute clg:inset-y-0 clg:left-0 clg:w-[46%]">
          <div className="flex flex-col gap-1.5 px-6 pt-8 pb-4">
            <h2 className="text-h5 text-trueBlack dark:text-white">{tourTitle}</h2>
            <p className="text-[13px] text-grey">{tourSubtitle}</p>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 pb-6">
            {steps.map((step, i) => {
              const isActive = i === activeIndex;

              return (
                <button
                  key={step.title}
                  onClick={() => {
                    setActiveIndex(i);
                    setProgress(0);
                  }}
                  className="group flex flex-col gap-2 text-left"
                >
                  {/* Only the playing step's bar fills; completed/other steps stay empty. */}
                  <div className="h-0.5 w-full overflow-hidden rounded-full bg-black-300">
                    <div className="h-full rounded-full bg-purpleGreen transition-[width] duration-150 ease-linear" style={{ width: `${(isActive ? progress : 0) * 100}%` }} />
                  </div>
                  <span className={cn("text-[15px] font-semibold transition-colors", isActive ? "text-trueBlack dark:text-white" : "text-grey group-hover:text-trueBlack dark:group-hover:text-white")}>
                    {step.title}
                  </span>
                  <p className={cn("text-[13px] leading-relaxed", isActive ? "text-grey opacity-100" : "text-grey opacity-50")}>{step.description}</p>
                </button>
              );
            })}
          </div>

          <div className="flex flex-col gap-3 border-t border-black-300 p-4">
            <button onClick={close} className="btn btn-primary-small w-full !h-10">
              {t("common:whats_new.tour.got_it")}
            </button>
            {expertPrompt}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FeatureTourModal;
